// Fiche d'un produit « Ma gamme » (création, consultation, modification) : produit, accords de distribution (rôles qui voient les
// achats), documents de conformité, stock et numéros de série. Fiche produit PDF au nom de la société (sans fabricant ni prix d'achat).
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useNavigate, useParams, useSearchParams, Link } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FileDown, Pencil, Plus, Trash2, Upload } from 'lucide-react';
import { api, messageErreur } from '../../lib/api.ts';
import { date, dateHeure, euros } from '../../lib/format.ts';
import { cleSerie } from '@shared/bons-intervention.ts';
import { useModules } from '../../lib/modules.ts';
import { useSession } from '../../lib/session.tsx';
import {
  Alerte, Badge, Bouton, Champ, ChampChoix, ChampTexte, Chargement, Dialogue, EntetePage, EtatErreur, EtatVide, FilContexte, Onglets, Panneau,
  useConfirmation, useNotifier,
} from '../../components/ui.tsx';
import { LIBELLES_DOCUMENTS, LIBELLES_PROTOCOLES, NATURES_DOCUMENTS, PROTOCOLES, type NatureDocument, type NatureProduit, type Protocole } from '@shared/gamme.ts';
import { BadgeNature, EncartFabricant, Protocoles, texteAlerteAccord, texteDelai, type AlerteAccord, type Produit, type ReglagesGamme } from './commun.tsx';
import { STATUTS_SERIE } from './PageGamme.tsx';

interface Palier { quantite_min: string; prix_net: string | null; remise_pct: string | null }
interface Accord {
  id: string; fournisseur: { id: string; nom: string | null } | null; prix_net: string | null; remise_pct: string | null; date_debut: string; date_fin: string | null;
  exclusivite: boolean; zone: string | null; notes: string | null; retire: boolean; version: number; etat: string; jours: number | null; paliers: Palier[];
}
interface Doc { id: string; nature: NatureDocument; titre: string; file_name: string; mime: string; valide_jusqu_au: string | null; cree_le: string; par: string | null }
interface Serie { id: string; numero: string; statut: string; date_pose: string | null; note: string | null; version: number; site_id: string | null; site_nom: string | null;
  client_nom: string | null; chantier_id: string | null; chantier_reference: string | null; chantier_titre: string | null; site_masque?: boolean }
interface Fiche {
  aujourdhui: string; droits: { ecrire: boolean; achats: boolean; tarif?: boolean }; reglages?: ReglagesGamme | null; produit: Produit; accords?: Accord[]; alerte_accord: AlerteAccord | null;
  documents: Doc[]; conformite: { nature: NatureDocument; libelle: string; etat: string; echeance: string | null; jours: number | null }[]; series: Serie[];
  /** Bons d'intervention où chaque n° apparaît (option bons active et droit bons.lire), par n° normalisé ; null = rien à afficher. */
  bons_series?: Record<string, BonCite[]> | null;
  /** Mouvements de stock (migration 048) : pose −1, retour +1, inventaire saisi ; les plus récents d'abord. */
  mouvements?: Mouvement[];
}
interface Mouvement { id: string; nature: 'pose' | 'retour' | 'inventaire'; quantite: string; stock_avant: string; stock_apres: string; insuffisant: boolean;
  note: string | null; numero: string | null; par: string | null; le: string }
const NATURES_MOUVEMENT: Record<Mouvement['nature'], string> = { pose: 'Pose', retour: 'Retour en stock', inventaire: 'Inventaire' };
const signe = (v: string) => { const n = Number(v); return `${n > 0 ? '+' : n < 0 ? '−' : ''}${Math.abs(n).toLocaleString('fr-FR')}`; };
interface BonCite { id: string; reference: string; version_no: number; type_libelle: string; statut: string; statut_libelle: string; date: string | null; site_nom: string }
type Onglet = 'produit' | 'accords' | 'documents' | 'series';
const ETATS_ACCORD: Record<string, { libelle: string; ton: 'succes' | 'ambre' | 'danger' | 'info' | 'neutre' }> = {
  en_vigueur: { libelle: 'En vigueur', ton: 'succes' }, fin_proche: { libelle: 'Fin proche', ton: 'ambre' }, echu: { libelle: 'Échu', ton: 'danger' },
  a_venir: { libelle: 'À venir', ton: 'info' }, retire: { libelle: 'Retiré', ton: 'neutre' },
};
const ETATS_DOC: Record<string, { libelle: string; ton: 'succes' | 'ambre' | 'danger' }> = {
  present: { libelle: 'Présent', ton: 'succes' }, a_renouveler: { libelle: 'À renouveler', ton: 'ambre' }, expire: { libelle: 'Expiré', ton: 'danger' },
  manquant: { libelle: 'Manquant', ton: 'danger' },
};
const ORIGINES: Record<string, string> = { palier: 'palier de l’accord', accord: 'accord de distribution', remise_fournisseur: 'remise négociée du fournisseur', base: 'prix de base' };
const versSaisie = (v: string | null | undefined) => (v === null || v === undefined ? '' : String(Number(v)).replace('.', ','));

export function PageProduit() {
  const { id } = useParams();
  const actif = useModules().ma_gamme;
  if (actif === false) return <Panneau><EtatVide titre="Fonction non activée" texte="Cette fonction n’est pas activée pour votre société." /></Panneau>;
  if (actif === undefined) return <Chargement />;
  if (id === 'nouveau') return <CreationProduit />;
  return <FicheProduit id={id!} />;
}

// ─── Formulaire produit (création et modification) ─────────────────────────────────────────────
interface Formulaire {
  nature: NatureProduit; designation: string; reference: string; marque: string; famille: string; description: string;
  caracteristiques: { libelle: string; valeur: string }[]; protocoles: Protocole[]; unite: string; prixVenteConseille: string; stock: string; actif: boolean;
  fabricant: string; referenceFabricant: string; fournisseurId: string; prixAchat: string;
}
const vide: Formulaire = { nature: 'maison', designation: '', reference: '', marque: '', famille: '', description: '', caracteristiques: [], protocoles: [],
  unite: 'u', prixVenteConseille: '', stock: '0', actif: true, fabricant: '', referenceFabricant: '', fournisseurId: '', prixAchat: '' };
const depuisProduit = (p: Produit): Formulaire => ({ nature: p.nature, designation: p.designation, reference: p.reference, marque: p.marque ?? '',
  famille: p.famille ?? '', description: p.description ?? '', caracteristiques: p.caracteristiques, protocoles: p.protocoles, unite: p.unite,
  prixVenteConseille: versSaisie(p.prix_vente_conseille), stock: versSaisie(p.stock), actif: p.actif,
  fabricant: p.achats?.fabricant ?? '', referenceFabricant: p.achats?.reference_fabricant ?? '', fournisseurId: p.achats?.fournisseur?.id ?? '', prixAchat: versSaisie(p.achats?.prix_achat) });
function corps(f: Formulaire, achats: boolean) {
  const n = (s: string) => (s.trim() ? s.trim() : null);
  return {
    nature: f.nature, designation: f.designation.trim(), reference: f.reference.trim(), marque: n(f.marque), famille: n(f.famille), description: n(f.description),
    caracteristiques: f.caracteristiques.filter((c) => c.libelle.trim() && c.valeur.trim()), protocoles: f.protocoles, unite: f.unite.trim() || 'u',
    prixVenteConseille: n(f.prixVenteConseille), stock: f.stock.trim() || '0', actif: f.actif,
    ...(achats ? { fabricant: n(f.fabricant), referenceFabricant: n(f.referenceFabricant), fournisseurId: f.fournisseurId || null, prixAchat: n(f.prixAchat) } : {}),
  };
}

function ChampsProduit({ f, set, achats, sansStock, edition }: { f: Formulaire; set: (x: Partial<Formulaire>) => void; achats: boolean; sansStock?: boolean; edition?: boolean }) {
  // Catalogue de la direction (01/11/2026) : en modification, la référence et l'activation sont réservées à l'« Accès achats ».
  const catalogueFige = !!edition && !achats;
  const fournisseurs = useQuery({ queryKey: ['fournisseurs'], enabled: achats, queryFn: () => api.get<{ id: string; name: string }[]>('/api/fournisseurs') });
  return (
    <div className="pile">
      <div className="segment mg-segment" role="group" aria-label="Nature du produit">
        <button type="button" aria-pressed={f.nature === 'maison'} onClick={() => set({ nature: 'maison' })}>Produit maison (votre marque)</button>
        <button type="button" aria-pressed={f.nature === 'distribue'} onClick={() => set({ nature: 'distribue' })}>Produit distribué</button>
      </div>
      <div className="grille-form">
        <Champ libelle="Désignation commerciale" required value={f.designation} onChange={(e) => set({ designation: e.target.value })} maxLength={200} />
        <Champ libelle="Référence maison" mono required value={f.reference} readOnly={catalogueFige} maxLength={120}
          aide={catalogueFige ? 'Référence fixée par la direction : elle ne se modifie qu’avec l’« Accès achats ».' : undefined}
          onChange={(e) => { if (!catalogueFige) set({ reference: e.target.value }); }} />
        <Champ libelle="Marque (vue par vos clients)" value={f.marque} onChange={(e) => set({ marque: e.target.value })} maxLength={120} />
        <Champ libelle="Famille" value={f.famille} onChange={(e) => set({ famille: e.target.value })} maxLength={80} placeholder="Ex. Passerelle" />
        <Champ libelle="Unité" value={f.unite} onChange={(e) => set({ unite: e.target.value })} maxLength={20} />
        {/* Tarif de la direction (décision du 01/11/2026) : modifiable avec « Accès achats » seulement ; lecture seule sinon. */}
        <Champ libelle="Prix de vente conseillé (€ HT)" inputMode="decimal" value={f.prixVenteConseille} readOnly={!achats}
          placeholder={achats ? undefined : 'À fixer par la direction'}
          aide={achats ? undefined : 'Tarif fixé par la direction : il s’applique aux devis et ne se modifie qu’avec l’« Accès achats ».'}
          onChange={(e) => { if (achats) set({ prixVenteConseille: e.target.value }); }} />
        {!sansStock && <Champ libelle="Stock initial" inputMode="decimal" value={f.stock} onChange={(e) => set({ stock: e.target.value })} />}
      </div>
      <ChampTexte libelle="Description" rows={3} value={f.description} onChange={(e) => set({ description: e.target.value })} maxLength={4000} />
      <fieldset className="mg-fieldset">
        <legend className="champ-libelle">Compatibilités</legend>
        <div className="mg-cases">
          {PROTOCOLES.map((p) => (
            <label key={p} className="case"><input type="checkbox" checked={f.protocoles.includes(p)}
              onChange={(e) => set({ protocoles: e.target.checked ? [...f.protocoles, p] : f.protocoles.filter((x) => x !== p) })} /> {LIBELLES_PROTOCOLES[p]}</label>
          ))}
        </div>
      </fieldset>
      <fieldset className="mg-fieldset">
        <legend className="champ-libelle">Caractéristiques</legend>
        {f.caracteristiques.map((c, i) => (
          <div key={i} className="mg-caracteristique">
            <Champ libelle="Caractéristique" value={c.libelle} maxLength={80} onChange={(e) => set({ caracteristiques: f.caracteristiques.map((x, j) => (j === i ? { ...x, libelle: e.target.value } : x)) })} />
            <Champ libelle="Valeur" value={c.valeur} maxLength={300} onChange={(e) => set({ caracteristiques: f.caracteristiques.map((x, j) => (j === i ? { ...x, valeur: e.target.value } : x)) })} />
            <Bouton variante="fantome" className="btn-icone" aria-label={`Retirer la caractéristique ${i + 1}`} icone={<Trash2 aria-hidden />}
              onClick={() => set({ caracteristiques: f.caracteristiques.filter((_, j) => j !== i) })} />
          </div>
        ))}
        <Bouton compact icone={<Plus aria-hidden />} onClick={() => set({ caracteristiques: [...f.caracteristiques, { libelle: '', valeur: '' }] })}>Ajouter une caractéristique</Bouton>
      </fieldset>
      {achats && (
        <fieldset className="mg-fieldset mg-interne">
          <legend className="champ-libelle">Achats — interne, jamais montré à vos clients ni aux techniciens</legend>
          <div className="grille-form">
            <Champ libelle="Fabricant réel" value={f.fabricant} onChange={(e) => set({ fabricant: e.target.value })} maxLength={200} />
            <Champ libelle="Référence du fabricant" mono value={f.referenceFabricant} onChange={(e) => set({ referenceFabricant: e.target.value })} maxLength={120} />
            <ChampChoix libelle="Fournisseur" vide="Aucun" value={f.fournisseurId} onChange={(e) => set({ fournisseurId: e.target.value })}
              options={(fournisseurs.data ?? []).map((x) => ({ valeur: x.id, libelle: x.name }))} />
            <Champ libelle="Prix d’achat de base (€ HT)" inputMode="decimal" value={f.prixAchat} onChange={(e) => set({ prixAchat: e.target.value })}
              aide="Vide = à renseigner (jamais 0 d’office)." />
          </div>
        </fieldset>
      )}
      <label className="case"><input type="checkbox" checked={f.actif} disabled={catalogueFige} onChange={(e) => set({ actif: e.target.checked })} /> Produit actif (proposé dans les devis)
        {catalogueFige && <span className="petit secondaire"> — réservé à la direction (accès achats)</span>}</label>
    </div>
  );
}

function CreationProduit() {
  const nav = useNavigate();
  const notifier = useNotifier();
  const qc = useQueryClient();
  const [f, setF] = useState<Formulaire>(vide);
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const { peut } = useSession();
  const achats = peut('gamme.achats');
  const envoyer = async (e?: FormEvent) => {
    e?.preventDefault();
    setErreur(null); setEnvoi(true);
    try {
      const r = await api.post<{ id: string }>('/api/gamme', corps(f, achats));
      await qc.invalidateQueries({ queryKey: ['gamme'] });
      notifier('Produit ajouté à votre gamme.');
      nav(`/gamme/${r.id}`, { replace: true });
    } catch (x) { setErreur(messageErreur(x)); } finally { setEnvoi(false); }
  };
  const pret = f.designation.trim() && f.reference.trim();
  return (
    <form className="pile-l mg-page" onSubmit={envoyer}>
      <EntetePage titre="Nouveau produit" fil={<FilContexte noeuds={[{ type: 'Espace', libelle: 'Ma gamme', to: '/gamme' }, { type: 'Produit', libelle: 'Nouveau' }]} />} />
      {f.nature === 'maison' && <EncartFabricant />}
      {erreur && <Alerte ton="danger">{erreur}</Alerte>}
      <Panneau><ChampsProduit f={f} set={(x) => setF({ ...f, ...x })} achats={achats} /></Panneau>
      <div className="barre-actions barre-collante" role="region" aria-label="Action principale">
        <span className="petit secondaire barre-collante-resume">{pret ? f.designation : 'Désignation et référence obligatoires'}</span>
        <Bouton type="submit" variante="principal" chargement={envoi} disabled={!pret}>Enregistrer le produit</Bouton>
      </div>
    </form>
  );
}

// ─── Fiche ─────────────────────────────────────────────────────────────────────────────────────
function FicheProduit({ id }: { id: string }) {
  const [params, setParams] = useSearchParams();
  const q = useQuery({ queryKey: ['gamme', 'fiche', id], queryFn: () => api.get<Fiche>(`/api/gamme/${id}`) });
  const [edition, setEdition] = useState(false);
  if (q.isLoading) return <Chargement />;
  if (q.isError || !q.data) return <EtatErreur erreur={q.error} reessayer={() => q.refetch()} />;
  const v = q.data; const p = v.produit;
  const onglets: { id: Onglet; libelle: string; compteur?: number }[] = [{ id: 'produit', libelle: 'Produit' },
    ...(v.accords ? [{ id: 'accords' as const, libelle: 'Accords', compteur: v.accords.filter((a) => !a.retire).length }] : []),
    { id: 'documents', libelle: 'Documents', compteur: v.documents.length }, { id: 'series', libelle: 'Stock et séries', compteur: v.series.length }];
  const demande = params.get('onglet') as Onglet | null;
  const onglet: Onglet = onglets.some((o) => o.id === demande) ? demande! : 'produit';
  const conformiteKo = v.conformite.filter((c) => c.etat !== 'present');
  return (
    <div className="pile-l mg-page">
      <EntetePage titre={p.designation}
        fil={<FilContexte noeuds={[{ type: 'Espace', libelle: 'Ma gamme', to: '/gamme' }, { type: 'Produit', libelle: p.reference }]} />}
        sousTitre={<span className="rangee"><span className="mono">{p.reference}</span><BadgeNature nature={p.nature} />{p.marque && <span>{p.marque}</span>}
          {p.demo && <Badge ton="ambre">Démo</Badge>}{!p.actif && <Badge>Désactivé</Badge>}</span>}
        actions={<>
          {v.droits.ecrire && <Bouton variante="principal" icone={<Pencil aria-hidden />} onClick={() => setEdition(true)}>Modifier</Bouton>}
          <a className="btn" href={`/api/gamme/${p.id}/fiche.pdf`} download><FileDown aria-hidden />Fiche produit PDF</a>
        </>} />
      {p.nature === 'maison' && <EncartFabricant />}
      {v.alerte_accord && <Alerte ton={v.alerte_accord.etat === 'echu' ? 'danger' : 'ambre'} titre="Accord de distribution">{texteAlerteAccord(v.alerte_accord, date)}.</Alerte>}
      {conformiteKo.length > 0 && (
        <Alerte ton="ambre" titre="Documents de conformité">
          {conformiteKo.map((c) => `${c.libelle} : ${ETATS_DOC[c.etat]?.libelle.toLowerCase() ?? c.etat}${c.echeance ? ` (${date(c.echeance)})` : ''}`).join(' ; ')}.
        </Alerte>
      )}
      <Onglets libelle="Fiche du produit" onglets={onglets} actif={onglet} changer={(o) => { const n = new URLSearchParams(params); n.set('onglet', o); setParams(n, { replace: true }); }} />
      {onglet === 'produit' && <OngletProduit v={v} />}
      {onglet === 'accords' && v.accords && <OngletAccords v={v} />}
      {onglet === 'documents' && <OngletDocuments v={v} />}
      {onglet === 'series' && <OngletSeries v={v} />}
      {edition && <DialogueEdition v={v} fermer={() => setEdition(false)} />}
    </div>
  );
}

function DialogueEdition({ v, fermer }: { v: Fiche; fermer: () => void }) {
  const qc = useQueryClient(); const notifier = useNotifier();
  const [f, setF] = useState<Formulaire>(() => depuisProduit(v.produit));
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const enregistrer = async () => {
    setErreur(null); setEnvoi(true);
    try {
      const { stock: _stock, ...modif } = corps(f, v.droits.achats);   // le stock se gère dans « Stock et séries » (inventaire, poses)
      await api.patch(`/api/gamme/${v.produit.id}`, { ...modif, version: v.produit.version });
      await qc.invalidateQueries({ queryKey: ['gamme'] });
      notifier('Produit enregistré.'); fermer();
    } catch (x) { setErreur(messageErreur(x)); } finally { setEnvoi(false); }
  };
  return (
    <Dialogue titre="Modifier le produit" ouvert fermer={fermer} large
      pied={<><Bouton onClick={fermer}>Annuler</Bouton><Bouton variante="principal" chargement={envoi} onClick={enregistrer}
        disabled={!f.designation.trim() || !f.reference.trim()}>Enregistrer</Bouton></>}>
      {erreur && <Alerte ton="danger">{erreur}</Alerte>}
      <ChampsProduit f={f} set={(x) => setF({ ...f, ...x })} achats={v.droits.achats} sansStock edition />
    </Dialogue>
  );
}

function OngletProduit({ v }: { v: Fiche }) {
  const p = v.produit;
  return (
    <div className="pile">
      <Panneau titre="Présentation">
        <div className="pile">
          {p.description ? <p className="mg-description">{p.description}</p> : <p className="petit discret">Aucune description.</p>}
          <div><span className="champ-libelle">Compatibilités</span><div><Protocoles liste={p.protocoles} /></div></div>
          {p.caracteristiques.length > 0 && (
            <dl className="mg-caracteristiques">{p.caracteristiques.map((c, i) => <div key={i}><dt>{c.libelle}</dt><dd>{c.valeur}</dd></div>)}</dl>
          )}
          {/* Le technicien ne voit aucun prix (décision du 01/11/2026) : le serveur ne lui sert pas le tarif. */}
          <p className="petit">{v.droits.tarif !== false && p.prix_vente_conseille !== undefined && (<>Prix de vente conseillé : <strong>{p.prix_vente_conseille === null ? 'à fixer par la direction' : euros(p.prix_vente_conseille)}</strong>{p.prix_vente_conseille === null ? '' : ' HT'}
            {' · '}</>)}Stock :<strong className="chiffre">{Number(p.stock).toLocaleString('fr-FR')}</strong> {p.unite}
            {Number(p.stock) < 0 && <> <Badge ton="ambre">Stock insuffisant</Badge></>}</p>
        </div>
      </Panneau>
      {p.achats && (
        <Panneau titre="Achats (interne)">
          <dl className="mg-caracteristiques">
            <div><dt>Fabricant réel</dt><dd>{p.achats.fabricant ?? <span className="inconnu">Non renseigné</span>}</dd></div>
            <div><dt>Référence du fabricant</dt><dd className="mono">{p.achats.reference_fabricant ?? <span className="inconnu">Non renseignée</span>}</dd></div>
            <div><dt>Fournisseur</dt><dd>{p.achats.fournisseur?.nom ?? <span className="inconnu">Aucun</span>}</dd></div>
            <div><dt>Prix d’achat de base</dt><dd>{p.achats.prix_achat === null ? <span className="inconnu">À renseigner</span> : euros(p.achats.prix_achat, 4)}</dd></div>
            {p.achats.prix_effectif && <div><dt>Prix d’achat appliqué (1 {p.unite})</dt>
              <dd>{p.achats.prix_effectif.prix === null ? <span className="inconnu">À renseigner</span> : euros(p.achats.prix_effectif.prix, 4)}
                <span className="tres-petit discret"> — {p.achats.prix_effectif.origine ? ORIGINES[p.achats.prix_effectif.origine] : p.achats.prix_effectif.detail}</span></dd></div>}
          </dl>
          <p className="tres-petit discret">Ces informations ne figurent jamais sur le devis, la fiche produit ni aucun document remis au client.</p>
        </Panneau>
      )}
    </div>
  );
}

// ─── Accords ───────────────────────────────────────────────────────────────────────────────────
interface FormAccord { fournisseurId: string; mode: 'prix' | 'remise'; valeur: string; dateDebut: string; dateFin: string; exclusivite: boolean; zone: string; notes: string;
  paliers: { quantiteMin: string; mode: 'prix' | 'remise'; valeur: string }[] }
function OngletAccords({ v }: { v: Fiche }) {
  const [edite, setEdite] = useState<Accord | 'nouveau' | null>(null);
  const qc = useQueryClient(); const notifier = useNotifier();
  const { confirmer, element } = useConfirmation();
  const retirer = async (a: Accord) => {
    if (!(await confirmer('Retirer l’accord ?', 'L’accord reste dans l’historique mais ne s’applique plus aux prix.', 'Retirer l’accord'))) return;
    try { await api.post(`/api/gamme/${v.produit.id}/accords/${a.id}/retirer`); await qc.invalidateQueries({ queryKey: ['gamme'] }); notifier('Accord retiré.'); }
    catch (x) { notifier(messageErreur(x), true); }
  };
  const accords = v.accords ?? [];
  return (
    <Panneau titre="Accords de distribution" actions={v.droits.ecrire ? <Bouton variante="principal" icone={<Plus aria-hidden />} onClick={() => setEdite('nouveau')}>Nouvel accord</Bouton> : undefined}>
      {element}
      <p className="petit secondaire">Le prix négocié au niveau du produit prime sur les remises générales du fournisseur.{v.reglages ? ` Alerte ${texteDelai(v.reglages.alerte_accord_jours)} avant la fin (délai réglé par votre société).` : ''}</p>
      {accords.length === 0 && <EtatVide titre="Aucun accord" texte="Saisissez le prix net ou la remise négociés, la période de validité et, si besoin, des paliers de volume." />}
      <ul className="mg-accords">
        {accords.map((a) => (
          <li key={a.id} className={`mg-accord${a.retire ? ' mg-retire' : ''}`}>
            <div className="rangee entre">
              <strong>{a.fournisseur?.nom ?? 'Fournisseur non précisé'}</strong>
              <Badge ton={ETATS_ACCORD[a.etat]?.ton ?? 'neutre'}>{ETATS_ACCORD[a.etat]?.libelle ?? a.etat}{a.etat === 'fin_proche' && a.jours !== null ? ` — ${a.jours} j` : ''}</Badge>
            </div>
            <span className="petit">{a.prix_net !== null ? `Prix net ${euros(a.prix_net)}` : a.remise_pct !== null ? `Remise ${versSaisie(a.remise_pct)} %` : 'Paliers seulement'}
              {' · '}du {date(a.date_debut)}{a.date_fin ? ` au ${date(a.date_fin)}` : ', sans échéance'}{a.exclusivite ? ' · exclusivité' : ''}{a.zone ? ` · zone : ${a.zone}` : ''}</span>
            {a.paliers.length > 0 && <span className="petit secondaire">Paliers : {a.paliers.map((x) => `dès ${versSaisie(x.quantite_min)} → ${x.prix_net !== null ? euros(x.prix_net) : `−${versSaisie(x.remise_pct)} %`}`).join(' ; ')}</span>}
            {a.notes && <span className="tres-petit discret">{a.notes}</span>}
            {v.droits.ecrire && !a.retire && (
              <div className="groupe-btn">
                <Bouton compact onClick={() => setEdite(a)}>Modifier</Bouton>
                <Bouton compact variante="danger" onClick={() => retirer(a)}>Retirer</Bouton>
              </div>
            )}
          </li>
        ))}
      </ul>
      {edite && <DialogueAccord produitId={v.produit.id} accord={edite === 'nouveau' ? null : edite} fermer={() => setEdite(null)} aujourdhui={v.aujourdhui} />}
    </Panneau>
  );
}

function DialogueAccord({ produitId, accord, fermer, aujourdhui }: { produitId: string; accord: Accord | null; fermer: () => void; aujourdhui: string }) {
  const qc = useQueryClient(); const notifier = useNotifier();
  const fournisseurs = useQuery({ queryKey: ['fournisseurs'], queryFn: () => api.get<{ id: string; name: string }[]>('/api/fournisseurs') });
  const [f, setF] = useState<FormAccord>(() => accord ? {
    fournisseurId: accord.fournisseur?.id ?? '', mode: accord.remise_pct !== null ? 'remise' : 'prix', valeur: versSaisie(accord.remise_pct ?? accord.prix_net),
    dateDebut: accord.date_debut, dateFin: accord.date_fin ?? '', exclusivite: accord.exclusivite, zone: accord.zone ?? '', notes: accord.notes ?? '',
    paliers: accord.paliers.map((x) => ({ quantiteMin: versSaisie(x.quantite_min), mode: x.remise_pct !== null ? 'remise' : 'prix', valeur: versSaisie(x.remise_pct ?? x.prix_net) })),
  } : { fournisseurId: '', mode: 'prix', valeur: '', dateDebut: aujourdhui, dateFin: '', exclusivite: false, zone: '', notes: '', paliers: [] });
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const set = (x: Partial<FormAccord>) => setF({ ...f, ...x });
  const enregistrer = async () => {
    setErreur(null); setEnvoi(true);
    const val = f.valeur.trim() || null;
    const b = {
      fournisseurId: f.fournisseurId || null, prixNet: f.mode === 'prix' ? val : null, remisePct: f.mode === 'remise' ? val : null,
      dateDebut: f.dateDebut, dateFin: f.dateFin || null, exclusivite: f.exclusivite, zone: f.zone.trim() || null, notes: f.notes.trim() || null,
      paliers: f.paliers.filter((x) => x.quantiteMin.trim() && x.valeur.trim()).map((x) => ({ quantiteMin: x.quantiteMin.trim(),
        ...(x.mode === 'prix' ? { prixNet: x.valeur.trim() } : { remisePct: x.valeur.trim() }) })),
      ...(accord ? { version: accord.version } : {}),
    };
    try {
      if (accord) await api.put(`/api/gamme/${produitId}/accords/${accord.id}`, b); else await api.post(`/api/gamme/${produitId}/accords`, b);
      await qc.invalidateQueries({ queryKey: ['gamme'] }); notifier('Accord enregistré.'); fermer();
    } catch (x) { setErreur(messageErreur(x)); } finally { setEnvoi(false); }
  };
  const modes = [{ valeur: 'prix', libelle: 'Prix net (€ HT)' }, { valeur: 'remise', libelle: 'Remise (%)' }];
  return (
    <Dialogue titre={accord ? 'Modifier l’accord' : 'Nouvel accord de distribution'} ouvert fermer={fermer} large
      pied={<><Bouton onClick={fermer}>Annuler</Bouton><Bouton variante="principal" chargement={envoi} disabled={!f.dateDebut} onClick={enregistrer}>Enregistrer l’accord</Bouton></>}>
      <div className="pile">
        {erreur && <Alerte ton="danger">{erreur}</Alerte>}
        <div className="grille-form">
          <ChampChoix libelle="Fournisseur" vide="Non précisé" value={f.fournisseurId} onChange={(e) => set({ fournisseurId: e.target.value })}
            options={(fournisseurs.data ?? []).map((x) => ({ valeur: x.id, libelle: x.name }))} />
          <ChampChoix libelle="Condition" value={f.mode} onChange={(e) => set({ mode: e.target.value as 'prix' | 'remise' })} options={modes} />
          <Champ libelle={f.mode === 'prix' ? 'Prix net (€ HT)' : 'Remise (%)'} inputMode="decimal" value={f.valeur} onChange={(e) => set({ valeur: e.target.value })}
            aide="Vide = seulement des paliers." />
          <Champ libelle="Début" type="date" required value={f.dateDebut} onChange={(e) => set({ dateDebut: e.target.value })} />
          <Champ libelle="Fin" type="date" value={f.dateFin} onChange={(e) => set({ dateFin: e.target.value })} aide="Vide = sans échéance." />
          <Champ libelle="Zone" value={f.zone} onChange={(e) => set({ zone: e.target.value })} maxLength={300} />
        </div>
        <label className="case"><input type="checkbox" checked={f.exclusivite} onChange={(e) => set({ exclusivite: e.target.checked })} /> Exclusivité</label>
        <fieldset className="mg-fieldset">
          <legend className="champ-libelle">Paliers de volume</legend>
          {f.paliers.map((x, i) => (
            <div key={i} className="mg-caracteristique">
              <Champ libelle="À partir de (quantité)" inputMode="decimal" value={x.quantiteMin} onChange={(e) => set({ paliers: f.paliers.map((y, j) => (j === i ? { ...y, quantiteMin: e.target.value } : y)) })} />
              <ChampChoix libelle="Condition" value={x.mode} options={modes} onChange={(e) => set({ paliers: f.paliers.map((y, j) => (j === i ? { ...y, mode: e.target.value as 'prix' | 'remise' } : y)) })} />
              <Champ libelle="Valeur" inputMode="decimal" value={x.valeur} onChange={(e) => set({ paliers: f.paliers.map((y, j) => (j === i ? { ...y, valeur: e.target.value } : y)) })} />
              <Bouton variante="fantome" className="btn-icone" aria-label={`Retirer le palier ${i + 1}`} icone={<Trash2 aria-hidden />} onClick={() => set({ paliers: f.paliers.filter((_, j) => j !== i) })} />
            </div>
          ))}
          <Bouton compact icone={<Plus aria-hidden />} onClick={() => set({ paliers: [...f.paliers, { quantiteMin: '', mode: 'prix', valeur: '' }] })}>Ajouter un palier</Bouton>
        </fieldset>
        <ChampTexte libelle="Notes" rows={2} value={f.notes} onChange={(e) => set({ notes: e.target.value })} maxLength={2000} />
      </div>
    </Dialogue>
  );
}

// ─── Documents ─────────────────────────────────────────────────────────────────────────────────
function OngletDocuments({ v }: { v: Fiche }) {
  const qc = useQueryClient(); const notifier = useNotifier();
  const { confirmer, element } = useConfirmation();
  const [nature, setNature] = useState<NatureDocument>('declaration_ue');
  const [titre, setTitre] = useState('');
  const [validite, setValidite] = useState('');
  const [fichier, setFichier] = useState<File | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const [cle, setCle] = useState(0);
  const deposer = async () => {
    if (!fichier) return;
    setEnvoi(true);
    const fd = new FormData();
    fd.append('meta', JSON.stringify({ nature, titre: titre.trim() || fichier.name.slice(0, 200), valideJusquAu: validite || null }));
    fd.append('fichier', fichier);
    try {
      await api.envoyer(`/api/gamme/${v.produit.id}/documents`, fd);
      await qc.invalidateQueries({ queryKey: ['gamme'] }); notifier('Document déposé.');
      setTitre(''); setValidite(''); setFichier(null); setCle((x) => x + 1);
    } catch (x) { notifier(messageErreur(x), true); } finally { setEnvoi(false); }
  };
  const retirer = async (d: Doc) => {
    if (!(await confirmer('Retirer le document ?', `« ${d.titre} » ne figurera plus sur la fiche (il reste conservé).`, 'Retirer'))) return;
    try { await api.post(`/api/gamme/${v.produit.id}/documents/${d.id}/retirer`); await qc.invalidateQueries({ queryKey: ['gamme'] }); }
    catch (x) { notifier(messageErreur(x), true); }
  };
  return (
    <div className="pile">
      {element}
      <Panneau titre="Suivi de la conformité">
        <ul className="mg-conformite">
          {v.conformite.map((c) => (
            <li key={c.nature} className="rangee entre"><span>{c.libelle}</span>
              <Badge ton={ETATS_DOC[c.etat]?.ton ?? 'neutre'}>{ETATS_DOC[c.etat]?.libelle ?? c.etat}{c.echeance ? ` — ${date(c.echeance)}` : ''}</Badge></li>
          ))}
        </ul>
      </Panneau>
      <Panneau titre="Documents et photos">
        {v.documents.length === 0 ? <EtatVide titre="Aucun document" texte="Photos, fiche technique, déclaration UE de conformité, notice, certificats." /> : (
          <ul className="mg-documents">
            {v.documents.map((d) => {
              const echu = d.valide_jusqu_au && d.valide_jusqu_au < v.aujourdhui;
              return (
                <li key={d.id} className="mg-document">
                  <div className="pile" style={{ gap: 2, minWidth: 0 }}>
                    <a href={`/api/gamme/${v.produit.id}/documents/${d.id}/fichier`} target="_blank" rel="noreferrer" className="mg-titre">{d.titre}</a>
                    <span className="tres-petit discret">{LIBELLES_DOCUMENTS[d.nature] ?? d.nature}{d.valide_jusqu_au ? ` · valable jusqu’au ${date(d.valide_jusqu_au)}` : ''}</span>
                  </div>
                  <div className="groupe-btn">
                    {echu && <Badge ton="danger">Expiré</Badge>}
                    {v.droits.ecrire && <Bouton compact variante="danger" onClick={() => retirer(d)}>Retirer</Bouton>}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Panneau>
      {v.droits.ecrire && (
        <Panneau titre="Déposer un document">
          <div className="grille-form">
            <ChampChoix libelle="Nature" value={nature} onChange={(e) => setNature(e.target.value as NatureDocument)}
              options={NATURES_DOCUMENTS.map((n) => ({ valeur: n, libelle: LIBELLES_DOCUMENTS[n] }))} />
            <Champ libelle="Titre" value={titre} onChange={(e) => setTitre(e.target.value)} maxLength={200} />
            <Champ libelle="Valable jusqu’au" type="date" value={validite} onChange={(e) => setValidite(e.target.value)} aide="Vide = sans échéance connue." />
            <div className="champ"><label className="champ-libelle" htmlFor="mg-fichier">Fichier (PDF, PNG ou JPEG)</label>
              <input key={cle} id="mg-fichier" type="file" className="saisie mg-fichier" accept="application/pdf,image/png,image/jpeg" onChange={(e) => setFichier(e.target.files?.[0] ?? null)} /></div>
          </div>
          <div className="groupe-btn" style={{ marginTop: 12 }}>
            <Bouton variante="principal" icone={<Upload aria-hidden />} disabled={!fichier} chargement={envoi} onClick={deposer}>Déposer</Bouton>
          </div>
        </Panneau>
      )}
    </div>
  );
}

// ─── Stock et numéros de série ─────────────────────────────────────────────────────────────────
function OngletSeries({ v }: { v: Fiche }) {
  const qc = useQueryClient(); const notifier = useNotifier();
  const [numeros, setNumeros] = useState('');
  const [siteId, setSiteId] = useState('');
  const [chantierId, setChantierId] = useState('');
  const [datePose, setDatePose] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [stock, setStock] = useState(versSaisie(v.produit.stock));
  useEffect(() => setStock(versSaisie(v.produit.stock)), [v.produit.stock]);
  const sites = useQuery({ queryKey: ['sites'], enabled: v.droits.ecrire, queryFn: () => api.get<{ id: string; name: string; client_name: string | null }[]>('/api/sites') });
  const chantiers = useQuery({ queryKey: ['chantiers', 'tous'], enabled: v.droits.ecrire, queryFn: () => api.get<any[]>('/api/chantiers?statut=tous').catch(() => []) });
  // La liste des chantiers porte le nom du site (le serveur revérifie que le chantier est bien sur le site choisi).
  const nomSite = (sites.data ?? []).find((x) => x.id === siteId)?.name;
  const chantiersDuSite = useMemo(() => (chantiers.data ?? []).filter((c: any) => !siteId || c.site_name === nomSite), [chantiers.data, siteId, nomSite]);
  const liste = numeros.split(/[\n,;]+/).map((x) => x.trim()).filter(Boolean);
  const ajouter = async () => {
    setEnvoi(true);
    try {
      await api.post(`/api/gamme/${v.produit.id}/series`, { numeros: liste, siteId: siteId || null, chantierId: chantierId || null, datePose: datePose || null });
      await qc.invalidateQueries({ queryKey: ['gamme'] }); notifier(`${liste.length} numéro${liste.length > 1 ? 's' : ''} de série enregistré${liste.length > 1 ? 's' : ''}.`);
      setNumeros('');
    } catch (x) { notifier(messageErreur(x), true); } finally { setEnvoi(false); }
  };
  const changerStatut = async (s: Serie, statut: string) => {
    try {
      const r = await api.patch<{ stock: { variation: number; stock: string; insuffisant: boolean } | null }>(`/api/gamme/${v.produit.id}/series/${s.id}`, { version: s.version, statut });
      await qc.invalidateQueries({ queryKey: ['gamme'] });
      if (r.stock) notifier(`${s.numero} : stock ${signe(String(r.stock.variation))} ${v.produit.unite}, maintenant ${Number(r.stock.stock).toLocaleString('fr-FR')}${r.stock.insuffisant ? ' — stock insuffisant, à régulariser' : ''}.`, r.stock.insuffisant);
    } catch (x) { notifier(messageErreur(x), true); }
  };
  const enregistrerStock = async (e: FormEvent) => {
    e.preventDefault();
    try { await api.patch(`/api/gamme/${v.produit.id}`, { version: v.produit.version, stock: stock.trim() || '0' }); await qc.invalidateQueries({ queryKey: ['gamme'] }); notifier('Stock enregistré.'); }
    catch (x) { notifier(messageErreur(x), true); }
  };
  return (
    <div className="pile">
      <Panneau titre="Stock">
        <div className="pile">
          <p className="mg-stock">Stock : <strong className="chiffre" data-testid="stock-produit">{Number(v.produit.stock).toLocaleString('fr-FR')}</strong> {v.produit.unite}
            {Number(v.produit.stock) < 0 && <> <Badge ton="ambre">Stock insuffisant</Badge></>}</p>
          {Number(v.produit.stock) < 0 && <Alerte ton="ambre" titre="Stock insuffisant">Des numéros ont été posés sans unité en stock : faites l’inventaire et saisissez la quantité réelle.</Alerte>}
          <p className="petit secondaire">Un numéro de série qui passe « Posé » retire une unité du stock ; « En stock » ou « Retourné » la remet. « Hors service » ne change rien.</p>
          {v.droits.ecrire && (
            <form className="mg-recherche" onSubmit={enregistrerStock}>
              <Champ libelle={`Inventaire : quantité réelle (${v.produit.unite})`} inputMode="decimal" value={stock} onChange={(e) => setStock(e.target.value)} />
              <Bouton type="submit">Enregistrer l’inventaire</Bouton>
            </form>
          )}
        </div>
      </Panneau>
      <Panneau titre="Numéros de série" serre>
        {v.series.length === 0 ? <div className="panneau-corps"><EtatVide titre="Aucun numéro de série" texte="Enregistrez les numéros de série reçus ou posés : vous les retrouverez depuis la recherche de Ma gamme." /></div> : (
          <ul className="liste">
            {v.series.map((s) => (
              <li key={s.id} className="liste-element mg-element mg-serie">
                <div className="liste-element-corps">
                  <span className="mono liste-element-titre">{s.numero}</span>
                  <span className="petit secondaire">{s.site_masque ? 'Site non autorisé pour votre compte'
                    : [s.client_nom, s.site_nom, s.chantier_reference ?? s.chantier_titre, s.date_pose ? `posé le ${date(s.date_pose)}` : null].filter(Boolean).join(' · ') || 'Non attribué'}</span>
                  {s.chantier_id && !s.site_masque && <Link className="petit" to={`/interventions/chantiers/${s.chantier_id}`}>Voir le chantier</Link>}
                  {v.bons_series && <BonsDeLaSerie bons={v.bons_series[cleSerie(s.numero)] ?? []} />}
                </div>
                {v.droits.ecrire ? (
                  <label className="champ mg-statut"><span className="visuellement-cache">Statut de {s.numero}</span>
                    <select className="saisie" value={s.statut} onChange={(e) => changerStatut(s, e.target.value)}>
                      {Object.entries(STATUTS_SERIE).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                    </select></label>
                ) : <Badge>{STATUTS_SERIE[s.statut] ?? s.statut}</Badge>}
              </li>
            ))}
          </ul>
        )}
      </Panneau>
      <MouvementsStock v={v} />
      {v.droits.ecrire && (
        <Panneau titre="Enregistrer des numéros de série">
          <div className="pile">
            <ChampTexte libelle="Numéros (un par ligne)" rows={3} value={numeros} onChange={(e) => setNumeros(e.target.value)} />
            <div className="grille-form">
              <ChampChoix libelle="Site (posé chez le client)" vide="En stock — non attribué" value={siteId} onChange={(e) => { setSiteId(e.target.value); setChantierId(''); }}
                options={(sites.data ?? []).map((s) => ({ valeur: s.id, libelle: s.client_name ? `${s.name} — ${s.client_name}` : s.name }))} />
              <ChampChoix libelle="Chantier" vide="Aucun" value={chantierId} onChange={(e) => setChantierId(e.target.value)}
                options={chantiersDuSite.map((c: any) => ({ valeur: c.id, libelle: c.reference ? `${c.reference} — ${c.title}` : c.title }))} />
              <Champ libelle="Date de pose" type="date" value={datePose} onChange={(e) => setDatePose(e.target.value)} />
            </div>
            <div className="groupe-btn"><Bouton variante="principal" disabled={!liste.length} chargement={envoi} onClick={ajouter}>
              {liste.length > 1 ? `Enregistrer ${liste.length} numéros` : 'Enregistrer le numéro'}</Bouton></div>
          </div>
        </Panneau>
      )}
    </div>
  );
}

/** Historique simple des mouvements de stock (50 derniers). */
function MouvementsStock({ v }: { v: Fiche }) {
  const liste = v.mouvements ?? [];
  return (
    <Panneau titre="Mouvements de stock" serre>
      {!liste.length ? <div className="panneau-corps"><p className="petit secondaire">Aucun mouvement : le stock bouge à la pose, au retour d’un numéro de série ou à l’inventaire.</p></div> : (
        <ul className="liste mg-mouvements" aria-label="Mouvements de stock">
          {liste.map((x) => (
            <li key={x.id} className="liste-element mg-mouvement">
              <div className="liste-element-corps">
                <span className="liste-element-titre">{NATURES_MOUVEMENT[x.nature]}{x.numero ? <> — <span className="mono">{x.numero}</span></> : null}</span>
                <span className="petit secondaire">{dateHeure(x.le)}{x.par ? ` · ${x.par}` : ''}{x.nature === 'inventaire' && x.note ? ` · ${x.note}` : ''}</span>
              </div>
              <span className="mg-mouvement-chiffres petit">
                <strong className="chiffre">{signe(x.quantite)} {v.produit.unite}</strong>
                <span className="secondaire chiffre">{Number(x.stock_avant).toLocaleString('fr-FR')} → {Number(x.stock_apres).toLocaleString('fr-FR')}</span>
                {x.insuffisant && <Badge ton="ambre">Stock insuffisant</Badge>}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Panneau>
  );
}

/** Bons d'intervention où le n° de série apparaît (calculé par le serveur selon les droits de lecture des bons). */
function BonsDeLaSerie({ bons }: { bons: BonCite[] }) {
  if (!bons.length) return <span className="petit discret">Cité dans aucun bon d’intervention que vous pouvez consulter.</span>;
  return (
    <ul className="mg-bons petit" aria-label="Bons d’intervention où ce numéro apparaît">
      {bons.map((b) => (
        <li key={b.id}>
          <Link className="mono" to={`/interventions/bons/${b.id}`}>{b.reference}{b.version_no > 1 ? ` v${b.version_no}` : ''}</Link>
          <span className="secondaire"> · {b.type_libelle}{b.date ? ` · ${date(b.date)}` : ''} · {b.site_nom}</span>{' '}
          <Badge ton={b.statut === 'signe' ? 'succes' : 'ambre'} icone={false}>{b.statut_libelle}</Badge>
        </li>
      ))}
    </ul>
  );
}
